import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Receipt } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, StatTile } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { toMinor, usePrintReceipt } from './receipt-print';

interface DepositRow {
  id: string;
  type: string;
  mode: string;
  amountMinor: number;
  txnRef: string | null;
  notes: string | null;
  receiptNo: string | null;
  receivedAt: string;
  receivedBy: { id: string; name: string | null } | null;
  shift: { id: string; counterName: string; openedAt: string; closedAt: string | null } | null;
}

interface AdmissionBalance {
  depositedMinor: number;
  billedMinor: number;
  paidMinor: number;
  balanceMinor: number;
  invoiceCount: number;
}

const MODES = ['cash', 'card', 'bank_transfer', 'mobile_wallet', 'online_gateway'];

export function AdmissionAdvances({ admissionId, admitted }: { admissionId: string; admitted: boolean }) {
  const { can } = useAuth();
  const [dialog, setDialog] = useState<'advance' | 'refund' | null>(null);
  const printReceipt = usePrintReceipt();
  const canManage = can('deposit.manage');

  const deposits = useQuery({
    queryKey: ['admission-deposits', admissionId],
    queryFn: () => get<DepositRow[]>(`/admissions/${admissionId}/deposits`),
  });
  const balance = useQuery({
    queryKey: ['admission-balance', admissionId],
    queryFn: () => get<AdmissionBalance>(`/admissions/${admissionId}/balance`),
  });

  const b = balance.data;

  return (
    <>
      {b && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label={tr('billing', 'advances_held')} value={money(b.depositedMinor)} />
          <StatTile label={tr('billing', 'billed')} value={money(b.billedMinor)} sub={tr('billing', 'x_invoice_s', { invoiceCount: b.invoiceCount })} />
          <StatTile label={tr('billing', 'paid')} value={money(b.paidMinor)} />
          <StatTile
            label={b.balanceMinor < 0 ? tr('billing', 'owing') : tr('billing', 'credit')}
            value={money(Math.abs(b.balanceMinor))}
            tone={b.balanceMinor < 0 ? 'destructive' : 'success'}
          />
        </div>
      )}

      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Link to={`/billing/statement?admissionId=${admissionId}`}>
          <Button variant="outline">{tr('billing', 'statement_of_account')}</Button>
        </Link>
        {canManage && (
          <Button variant="outline" onClick={() => setDialog('refund')}>
            {tr('billing', 'refund_advance')}
          </Button>
        )}
        {canManage && admitted && <Button onClick={() => setDialog('advance')}>{tr('billing', 'collect_advance')}</Button>}
      </div>

      <DataTable
        rows={deposits.data}
        loading={deposits.isLoading} refreshing={deposits.isFetching}
        error={deposits.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_advances_have_been_taken_for_this_admission')}
        columns={[
          { header: tr('billing', 'when'), cell: (row) => dateTime(row.receivedAt) },
          { header: tr('billing', 'receipt'), cell: (row) => <span className="tabular text-xs">{row.receiptNo ?? '—'}</span> },
          { header: tr('billing', 'type'), cell: (row) => humanize(row.type) },
          { header: tr('billing', 'mode'), cell: (row) => humanize(row.mode) },
          { header: tr('billing', 'reference'), cell: (row) => row.txnRef ?? '—' },
          {
            header: tr('billing', 'taken_by'),
            cell: (row) => (
              <div className="text-xs">
                <p>{row.receivedBy?.name ?? '—'}</p>
                <p className="text-muted-foreground">{row.shift ? `${row.shift.counterName}${row.shift.closedAt ? ' (closed)' : ''}` : tr('billing', 'no_shift')}</p>
              </div>
            ),
          },
          {
            header: tr('billing', 'amount'),
            align: 'right',
            cell: (row) => <span className={row.amountMinor < 0 ? 'text-destructive' : undefined}>{money(row.amountMinor)}</span>,
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.type === 'applied' ? null : (
                <div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" aria-label={tr('billing', 'print_receipt')} onClick={() => printReceipt.mutate({ kind: 'deposit', id: row.id, size: 'thermal' })}>
                    <Receipt className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" aria-label={tr('billing', 'print_receipt_on_a4')} onClick={() => printReceipt.mutate({ kind: 'deposit', id: row.id, size: 'A4' })}>
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              ),
          },
        ]}
      />

      {dialog && <AdvanceDialog admissionId={admissionId} kind={dialog} onClose={() => setDialog(null)} />}
    </>
  );
}

function AdvanceDialog({ admissionId, kind, onClose }: { admissionId: string; kind: 'advance' | 'refund'; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const printReceipt = usePrintReceipt();
  const [type, setType] = useState<'advance' | 'top_up'>('advance');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [txnRef, setTxnRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; receiptNo: string | null } | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());

  const refunding = kind === 'refund';
  const amountMinor = toMinor(amount);

  const save = useMutation({
    mutationFn: () =>
      post<{ id: string; receiptNo: string | null }>(
        '/deposits',
        {
          admissionId,
          amountMinor,
          type: refunding ? 'refund' : type,
          mode,
          ...(txnRef ? { txnRef } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { idempotencyKey },
      ),
    onSuccess: (deposit) => {
      toast.success(refunding ? tr('billing', 'advance_refunded') : tr('billing', 'advance_recorded'));
      setIdempotencyKey(newIdempotencyKey());
      setDone({ id: deposit.id, receiptNo: deposit.receiptNo });
      void queryClient.invalidateQueries({ queryKey: ['admission-deposits', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['admission-balance', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['admission', admissionId] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (done) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={refunding ? tr('billing', 'advance_refunded') : tr('billing', 'advance_recorded')}
        description={done.receiptNo ? tr('billing', 'receipt_x', { receiptNo: done.receiptNo }) : undefined}
        footer={
          <>
            <Button variant="outline" onClick={() => printReceipt.mutate({ kind: 'deposit', id: done.id, size: 'A4', original: true })}>
              <Printer className="h-4 w-4" /> {tr('billing', 'a4_receipt')}
            </Button>
            <Button variant="outline" onClick={() => printReceipt.mutate({ kind: 'deposit', id: done.id, size: 'thermal', original: true })}>
              <Receipt className="h-4 w-4" /> {tr('billing', 'thermal_receipt')}
            </Button>
            <Button onClick={onClose}>{tr('billing', 'done')}</Button>
          </>
        }
      >
        <Alert variant="success">
          {money(amountMinor)} {refunding ? tr('billing', 'paid_back') : tr('billing', 'received')} {tr('billing', 'by')} {humanize(mode)}.
        </Alert>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={refunding ? tr('billing', 'refund_an_advance') : tr('billing', 'collect_an_advance')}
      description={refunding ? tr('billing', 'only_what_is_left_after_the_bill_is_settled_and') : tr('billing', 'held_as_a_liability_until_it_is_applied_to_the')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={amountMinor <= 0 || (refunding && notes.trim().length < 3)}
          >
            {refunding ? tr('billing', 'refund') : tr('billing', 'record_advance')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!refunding && (
          <Field label={tr('billing', 'kind')}>
            <ChoiceButtons<'advance' | 'top_up'>
              value={type}
              onChange={setType}
              options={[
                { value: 'advance', label: tr('billing', 'advance') },
                { value: 'top_up', label: tr('billing', 'top_up') },
              ]}
            />
          </Field>
        )}
        <Field label={tr('billing', 'amount')} required>
          <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="tabular text-lg" autoFocus />
        </Field>
        <Field label={tr('billing', 'mode')} required>
          <ChoiceButtons layout="chips" value={mode} onChange={setMode} options={MODES.map((m) => ({ value: m, label: humanize(m, 'payment_mode') }))} />
        </Field>
        {mode !== 'cash' && (
          <Field label={tr('billing', 'reference')} hint={tr('billing', 'the_card_slip_transfer_or_wallet_reference')}>
            <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} />
          </Field>
        )}
        <Field label={refunding ? tr('billing', 'reason') : tr('billing', 'note')} required={refunding}>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={400} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
